/* eslint max-depth: ["error", 3] */
import { captureException } from '@chilipiper/sentry'
import { getValue } from '@chilipiper/config'
import { Element } from '@xmpp/xml'
import { TimeoutError } from '@xmpp/events'
import * as R from 'remeda'
import { nanoid } from 'nanoid'
import invariant from 'tiny-invariant'
import { Affiliations, Marker, Room } from '../chat-data/types'
import { exponentialSleep } from '../helpers/exponentialSleep'
import { escapeEvent } from '../helpers/event-escaping/eventEscaping'
import {
  INBOX_XMLNS,
  MAM_XMLNS,
  MARKERS_XMLNS,
  MUC_DOMAIN,
  MUC_LIGHT_AFFILIATIONS,
  PAGINATION_XMLNS,
  SM_XMLNS,
} from './constants'
import {
  onAffiliationsStanza,
  onArchivedAffiliationsStanza,
  onArchivedGroupchatMarkerStanza,
  onArchiveGroupchatStanza,
  onConversationStatusChange,
  onFinInboxStanza,
  onGroupchatFailedStanza,
  onGroupchatMarkerStanza,
  onGroupchatStanza,
  onInboxStanza,
} from './listeners'
import { parseInboxItemStanza } from './parsers/inboxStanza'
import { PayloadIn, TypedEnvelopeOut } from './types'
import { client as createXMPPClient, xml } from './xmpp-lib'
import { getUserAffiliationData } from './helpers'

export interface Credentials {
  refreshToken?: string
  token: string
  username: string
}

export type ConnectionStatus =
  | 'CONNECTED'
  | 'CONNECTING'
  | 'RECONNECTING'
  | 'ERROR'
  | 'DISCONNECTED'
export type EventStatus = 'displayed' | 'failed'

export interface Listeners {
  onConversationStatusChange: (event: Extract<PayloadIn, { type: 'StatusChange' }>) => void
  onGroupchatMarker: (marker: Marker) => void
  onGroupchatMessage: (event: PayloadIn) => void
  onGroupchatMessageFailed: (event: Extract<PayloadIn, { type: 'Error' }>) => void
  onAffiliationChange: (event: Affiliations) => void
  onArchivedAffiliationsChange: (event: Affiliations) => void
}

export type XMPPClientType = ReturnType<typeof createClient>

export const createClient = (id: string) => {
  type Client = ReturnType<typeof createXMPPClient>
  let client: Client | undefined

  let lastJid: Client['jid'] | null = null
  const getJid = (client: Client | undefined) => {
    if (!client?.jid) {
      return lastJid
    }
    lastJid = client.jid
    return client.jid
  }
  /** A queue of messages to send. Needed to handle possible connection loss while sending messages. */
  let messagesQueue: Array<{
    element: Element
    onError: (error: Error) => void
    onSuccess: (element: Element) => void
    type: 'element' | 'iq'
  }> = []

  /** Tries sending an element to the server */
  const sendElementRaw = async (type: 'element' | 'iq', element: Element): Promise<Element> => {
    invariant(client, 'Authenticate before sending element')
    if (type === 'iq') {
      try {
        return await client.iqCaller.request(element)
      } catch (error) {
        // Ignore timeout errors because:
        // 1. We send IQ and connection closes
        // 2. We retry the IQ in next connection and get a response
        // @xmpp library throws TimeoutError anyway
        if (error instanceof Error && error.name === 'TimeoutError') {
          // never resolve because we will retry the message
          return new Promise(R.doNothing())
        }
        console.error(error)
        throw error
      }
    }
    try {
      await client.send(element)
    } catch (error) {
      console.error(error)
      throw error
    }
    return element
  }

  /**
   * If client is online sends the element. If it doesn't succeed, it will retry after 15 seconds
   * and if it still fails, it will reject after 30 seconds total.
   *
   * If there's no id on the element we have no way to confirm message was sent so we don't retry.
   */
  const sendElementWithRetry = (type: 'element' | 'iq', element: Element) => {
    return new Promise<Element>((resolve, reject) => {
      if (!client) {
        return reject(new Error('Authenticate before sending element'))
      }
      if (client.status === 'online') {
        const id = element.getAttr('id')
        if (id) {
          const retryTimeoutId = setTimeout(() => {
            sendElementRaw(type, element).catch(rejectWithCleanup)
          }, 15 * 1000)
          const rejectTimeoutId = setTimeout(() => {
            rejectWithCleanup(new Error('Element timeout'))
          }, 30 * 1000)

          const cleanup = () => {
            clearTimeout(retryTimeoutId)
            clearTimeout(rejectTimeoutId)
            client?.removeListener('close', handleClientConnectionClose)
            client?.removeListener('stanza', resolveWithClosingStanza)
          }
          const rejectWithCleanup = (error: Error) => {
            cleanup()
            reject(error)
          }
          const handleClientConnectionClose = () => {
            cleanup()
            // no need to reject because we're already calling onError it for all messages in messagesQueue
          }
          client.addListener('close', handleClientConnectionClose)
          const resolveWithClosingStanza = (stanza: Element) => {
            if (stanza.getAttr('id') === id) {
              cleanup()
              resolve(stanza)
            }
          }
          client.addListener('stanza', resolveWithClosingStanza)

          sendElementRaw(type, element).catch(rejectWithCleanup)
        } else {
          sendElementRaw(type, element).then(resolve).catch(reject)
        }
      }
    })
  }

  /** True from when client went offline until queued messages are sent, False otherwise */
  let isPendingSendingQueuedMessagesFromPreviousConnection = true

  /**
   * Queues an element to be sent. If sendFirst is true, the element is queued in the front,
   * otherwise it's added to the end.
   *
   * If it's the first element in the queue and we're not currently sending queued messages from the
   * previous connection, we start sending queued messages.
   */
  const sendElement = async (
    type: 'element' | 'iq',
    element: Element,
    options?: { sendFirst?: boolean }
  ) => {
    return new Promise<Element>((resolve, reject) => {
      const message = {
        type,
        element,
        onSuccess: resolve,
        onError: reject,
      }
      if (options?.sendFirst) {
        messagesQueue.unshift(message)
      } else {
        messagesQueue.push(message)
      }
      // The first sendElement call will start the queue
      if (messagesQueue.length > 1) {
        return
      }

      // We don't want to send if we're pending queued messages because it could lead to duplicates
      if (!isPendingSendingQueuedMessagesFromPreviousConnection) {
        sendAndEmptyMessagesQueue()
      }
    })
  }

  let latestCall = 0
  /**
   * Sends all queued messages and removes the sent elements from the queue. If a callback is
   * provided, it's called after the queue is emptied.
   */
  const sendAndEmptyMessagesQueue = async (cb?: () => void) => {
    latestCall += 1
    const thisCall = latestCall
    // Improve resistance to race conditions by checking if it's the latest call
    while (messagesQueue.length > 0 && thisCall === latestCall) {
      const messagesQueueCopy = [...messagesQueue]
      await Promise.all(
        messagesQueueCopy.map(async message => {
          const { element, onSuccess, onError, type } = message
          try {
            onSuccess(await sendElementWithRetry(type, element))
          } catch (error) {
            console.error(error)
            if (error instanceof Error) {
              onError(error)
            } else {
              onError(new Error(String(error)))
            }
          }
          // We need to atomically remove the message from the queue to prevent duplicates when we call sendAndEmptyMessagesQueue again on reconnection
          messagesQueue = messagesQueue.filter(el => el !== message)
        })
      )
    }
    cb?.()
  }

  /** Creates a new XMPP client and authenticates it with the given credentials. */
  const auth = (credentials: Credentials, tenantId: string) => {
    const origin = typeof window !== 'undefined' ? window.origin : ''
    client = createXMPPClient({
      service: `${origin.replace(
        'http',
        'ws'
      )}/api/im/public/tenant/${tenantId}/websockets/?id=${id}`,
      domain: 'localhost',
      credentials,
    })
    client.setMaxListeners(100)
    client.on('disconnect', () => {
      isPendingSendingQueuedMessagesFromPreviousConnection = true
    })

    let reconnectionTimeout: ReturnType<typeof setTimeout> | undefined

    client.on('connect', () => {
      // Load balancer kills connection every REACT_APP_XMPP_TIMEOUT_SECONDS seconds, but sometimes we still think we're connected.
      // Make sure we use a working connection
      clearTimeout(reconnectionTimeout)
      reconnectionTimeout = setTimeout(
        async () => {
          try {
            await client?.disconnect()
          } catch (e) {
            if (e instanceof TypeError) {
              // It's most likely an issue in xmpp library, we should handle it gracefully as socket is probably already closed
              return
            }
            if (e instanceof TimeoutError) {
              // The socket is likely already closed the close event inside xmpp library wasn't emitted before it timed out making throw here
              // https://github.com/xmppjs/xmpp.js/blob/ee361dc968cde84dfb4295d840c534d36bcecfc3/packages/connection/index.js#L245
              return
            }
            throw e
          }
        },
        Number(getValue('REACT_APP_XMPP_TIMEOUT_SECONDS') || 60) * 1000
      )
    })

    client.on('element', element => {
      invariant(client, 'Authenticate before listening to elements')
      // Resumed is sent when resumed stream
      // Enabled is sent when established a new stream
      if (
        (element.name === 'resumed' || element.name === 'enabled') &&
        element.getAttr('xmlns') === SM_XMLNS
      ) {
        handleNewWebsocketConnection()
      }
    })
  }

  const handleNewWebsocketConnection = () => {
    // We need to schedule a task so status can switch from open to online
    setTimeout(async () => {
      invariant(client, 'Authenticate before listening to send')
      if (client.status === 'online') {
        await sendAndEmptyMessagesQueue(() => {
          isPendingSendingQueuedMessagesFromPreviousConnection = false
        })
      } else {
        captureException(
          new Error(
            `Tried retransmitting messages queue, but status is not online got: ${client.status}`
          )
        )
      }
    }, 0)
  }

  /** Load some inbox rooms from a specific box with pagination */
  const loadInbox = async (params: {
    after?: string
    box: 'inbox' | 'archive' | 'all'
    max: number
  }) => {
    invariant(client, 'Authenticate before loading inbox')
    const currentLoadInboxId = nanoid()

    const pagination = [xml('max', {}, params.max.toString())]
    if (params.after) {
      pagination.push(xml('after', {}, params.after))
    }

    const query = xml(
      'iq',
      { type: 'set', id: currentLoadInboxId },
      xml(
        'inbox',
        { xmlns: INBOX_XMLNS, queryid: currentLoadInboxId },
        xml(
          'x',
          { xmlns: 'jabber:x:data', type: 'form' },
          xml('field', { var: 'FORM_TYPE', type: 'hidden' }, xml('value', {}, INBOX_XMLNS)),
          xml('field', { var: 'box', type: 'list-single' }, xml('value', {}, params.box))
        ),
        xml('set', PAGINATION_XMLNS, ...pagination)
      )
    )

    let rooms: Array<Room> = []
    const unsubscribe = onInboxStanza(client, room => {
      if (room.queryId === currentLoadInboxId) {
        rooms.push(room)
      }
    })
    const unsubscribeFinInboxStanza = onFinInboxStanza(client, ({ queryId, lastId }) => {
      if (queryId === currentLoadInboxId) {
        rooms = rooms.map(room => ({ ...room, lastId }))
      }
    })
    await sendElement('iq', query)
    unsubscribe()
    unsubscribeFinInboxStanza()
    return rooms
  }

  /** Loads room metadata like status and isMuted */
  const loadInboxItem = async (roomId: string) => {
    invariant(client, 'Authenticate before loading inbox item')
    const query = xml(
      'iq',
      { type: 'get' },
      xml('query', {
        xmlns: `${INBOX_XMLNS}#conversation`,
        jid: `${roomId}@${MUC_DOMAIN}`,
        complete: true,
      })
    )
    const stanza = await sendElement('iq', query)
    return parseInboxItemStanza(stanza)
  }

  /**
   * Loads room events with pagination
   *
   * @param params.max - The maximum number of events to load. If number of loaded events is less
   *   than max, it means that there are no more events to load.
   * @param params.waitForRoomPreparation - If true and before param is not defined, the client will
   *   wait for the room to be prepared before loading events.
   */
  const loadRoomEvents = async (
    roomId: string,
    params: { before?: string; max: number; waitForRoomPreparation?: boolean }
  ) => {
    invariant(client, 'Authenticate before loading room events')

    const pagination = [xml('max', {}, params.max.toString()), xml('before')]
    if (params.before) {
      pagination.push(xml('before', {}, params.before))
    }

    const queryId = nanoid()

    const query = xml(
      'iq',
      { type: 'set', to: `${roomId}@${MUC_DOMAIN}` },
      xml(
        'query',
        { xmlns: MAM_XMLNS, queryid: queryId },
        xml('x', 'jabber:x:data', xml('field', { var: 'simple' }, xml('value', {}, 'true'))),
        xml('set', PAGINATION_XMLNS, ...pagination)
      )
    )

    const events: Array<PayloadIn | Marker | Affiliations> = []

    const unsubscribeEvents = onArchiveGroupchatStanza(
      client,
      event => events.push(event),
      stanza => stanza.getChild('result')?.getAttr('queryid') === queryId
    )
    const unsubscribeMarkers = onArchivedGroupchatMarkerStanza(
      client,
      marker => events.push(marker),
      stanza => stanza.getChild('result')?.getAttr('queryid') === queryId
    )
    const unsubscribeAffiliations = onArchivedAffiliationsStanza(
      client,
      affiliations => events.push(affiliations),
      stanza => stanza.getChild('result')?.getAttr('queryid') === queryId
    )

    // When using pagination it's possible there are no messages left to fetch
    if (params.before === undefined && params.waitForRoomPreparation) {
      // We need to pool because it's possible we send this iq before BE prepares the room
      for (let i = 0; i < 8; i++) {
        if (i > 0) {
          // min 200ms, max 12.8s (total 25.4s)
          await exponentialSleep(i, 100)
        }
        await sendElement('iq', query)
        if (events.length > 0) {
          break
        }
      }
    } else {
      await sendElement('iq', query)
    }

    unsubscribeEvents()
    unsubscribeMarkers()
    unsubscribeAffiliations()

    return events
  }

  /**
   * Sends an event to a room.
   *
   * @param roomId - The ID of the room to send the event to.
   * @param event - The event to send.
   * @param eventId - The ID of the event. If not provided, a new ID will be generated.
   */
  const sendEvent = async (roomId: string, event: TypedEnvelopeOut, eventId = nanoid()) => {
    const jid = getJid(client)
    invariant(jid, 'Authenticate before sending event')
    const message = xml(
      'message',
      {
        type: 'groupchat',
        from: `${jid.local}@${jid.domain}`,
        to: `${roomId}@${MUC_DOMAIN}`,
        id: eventId,
      },
      xml('body', {}, escapeEvent(JSON.stringify(event))),
      xml('markable', MARKERS_XMLNS)
    )

    await sendElement('element', message)
  }

  /** Sets the mute status of a room. */
  const setMuteStatus = (roomId: string, isMuted: boolean) => {
    const jid = getJid(client)
    invariant(jid, 'Authenticate before sending event')

    const secondsInCentury = 60 * 60 * 24 * 365 * 100
    const query = xml(
      'iq',
      { type: 'set' },
      xml(
        'query',
        { xmlns: `${INBOX_XMLNS}#conversation`, jid: `${roomId}@${MUC_DOMAIN}` },
        xml('mute', {}, isMuted ? secondsInCentury.toString() : '0')
      )
    )

    return sendElement('iq', query)
  }

  /** Marks an event as read or failed. */
  const markEvent = async (roomId: string, eventId: string, status: EventStatus, id = nanoid()) => {
    const jid = getJid(client)
    invariant(jid, 'Authenticate before sending event')
    const message = xml(
      'message',
      {
        type: 'groupchat',
        from: `${jid.local}@${jid.domain}`,
        to: `${roomId}@${MUC_DOMAIN}`,
        id,
      },
      xml(status, { id: eventId, xmlns: MARKERS_XMLNS })
    )

    await sendElement('element', message)
  }

  /** Stops the client and clears the queue rejecting all pending messages. */
  const stop = async () => {
    for (const { onError } of messagesQueue) {
      onError(new Error('client stopped'))
    }
    messagesQueue = []
    client?.removeAllListeners()
    await client?.stop()
  }

  /** Listens to connection status changes. */
  const onConnectionStatusChange = (handler: (status: ConnectionStatus) => void) => {
    invariant(client, 'Authenticate before listening to status change')
    client.reconnect.on('reconnecting', () => {
      handler('RECONNECTING')
    })

    client.reconnect.on('reconnected', () => {
      handler('CONNECTED')
    })
  }

  /** Sets up listeners for various stanzas. */
  const setupListeners = (listeners: Listeners) => {
    invariant(client, 'Authenticate before setting up listeners')

    onConversationStatusChange(client, listeners.onConversationStatusChange)
    onGroupchatStanza(client, listeners.onGroupchatMessage)
    onGroupchatMarkerStanza(client, listeners.onGroupchatMarker)
    onGroupchatFailedStanza(client, listeners.onGroupchatMessageFailed)
    onAffiliationsStanza(client, listeners.onAffiliationChange)
    onArchivedAffiliationsStanza(client, listeners.onArchivedAffiliationsChange)
  }

  const start = (): Promise<void> => {
    invariant(client, 'Authenticate before starting client')
    const currentClient = client

    return new Promise((resolve, reject) => {
      const errorHandler = (error: Error) => {
        currentClient.removeAllListeners()
        currentClient
          .stop()
          .catch(R.doNothing())
          .finally(() => reject(error))
      }
      currentClient.once('error', errorHandler)
      currentClient.once('connect', () => {
        currentClient.removeListener('error', errorHandler)
        resolve()
      })
      currentClient.start().catch(errorHandler)
    })
  }

  const onError = (errorHandler: (error: unknown, cleanupClient: () => Promise<void>) => void) => {
    invariant(client, 'Authenticate before starting client')
    const currentClient = client

    currentClient.once('error', error => {
      errorHandler(error, async () => {
        currentClient.removeAllListeners()
        await currentClient.stop().catch(R.doNothing())
      })
    })
  }

  /** Gets the current affiliations of a room. */
  const getAffiliations = async (
    roomId: string
  ): Promise<{
    version: string
    users: Array<{ id: string; affiliation: 'member' | 'none' | 'owner' }>
  }> => {
    invariant(client, 'Authenticate before getting roster')
    const query = xml(
      'iq',
      { type: 'get', to: `${roomId}@${MUC_DOMAIN}` },
      xml('query', { xmlns: MUC_LIGHT_AFFILIATIONS })
    )
    const stanza = await sendElement('iq', query)

    const version = stanza.getChild('query')?.getChild('version')?.getText()
    invariant(version, 'Version not found in the stanza')
    const users = stanza.getChild('query')?.getChildren('user').map(getUserAffiliationData)
    invariant(users, 'Users not found in the stanza')

    return { version, users }
  }

  return {
    loadInbox,
    loadRoomEvents,
    sendEvent,
    markEvent,
    auth,
    start,
    stop,
    onError,
    onConnectionStatusChange,
    setupListeners,
    loadInboxItem,
    getAffiliations,
    setMuteStatus,
  }
}
